export interface TransportConfig {
  endpoint: string;
  writeKey: string;
  maxRetries: number;
  debug: boolean;
  /** Dry-run: transport aceita payload e retorna sucesso sem enviar. */
  shadow?: boolean;
  /**
   * Reserved for future per-domain transport overrides. The previous
   * direct-to-legacy-collector path is gone — the in.bri.us upstream
   * requires a server-side `api_key` that must never live in the
   * browser, so the SDK now always posts `ingest_http_v2` to the
   * interceptor worker, which owns the transform + api_key.
   */
  legacyMode?: boolean;
}

export interface TransportPayload {
  schemaVersion: "ingest_http_v2";
  sentAt: string;
  sdk: { name: string; version: string };
  batch: unknown[];
}

const TIMEOUT_MS = 5_000;

const NON_RETRYABLE_STATUSES = new Set([400, 401, 422]);

function isRetryableStatus(status: number): boolean {
  return status === 429 || status === 503 || (status >= 500 && status <= 599);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function encodeAuth(writeKey: string): string {
  return btoa(`${writeKey}:`);
}

/**
 * Error thrown by `transport.send` when all retry attempts are exhausted or
 * the server returns a non-retryable status. Callers should treat this as
 * a signal to persist the batch (offline queue, DLQ, etc.).
 */
export class TransportError extends Error {
  readonly attempts: number;
  readonly status?: number;
  readonly kind: "non-retryable" | "retry-exhausted" | "network";

  constructor(
    message: string,
    opts: {
      attempts: number;
      status?: number;
      kind: "non-retryable" | "retry-exhausted" | "network";
      cause?: unknown;
    },
  ) {
    super(message, opts.cause !== undefined ? { cause: opts.cause } : undefined);
    this.name = "TransportError";
    this.attempts = opts.attempts;
    this.status = opts.status;
    this.kind = opts.kind;
  }
}

export function createTransport(config: TransportConfig) {
  /**
   * Sends a payload to the ingest endpoint with exponential backoff retries.
   *
   * Throws `TransportError` when retries are exhausted, the server returns
   * a non-retryable status (400/401/422), or an unknown status code is
   * observed. Never returns a boolean — success is indicated by a resolved
   * promise. Callers MUST wrap this in try/catch and handle the error path
   * (persist to offline queue, bump metrics, etc.).
   */
  async function send(payload: TransportPayload): Promise<void> {
    if (config.shadow) {
      if (config.debug) {
        // biome-ignore lint/suspicious/noConsole: shadow dry-run observability
        console.info("[ets] shadow: dropped batch of", payload.batch.length, "events");
      }
      return;
    }

    const body = JSON.stringify(payload);
    const baseHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Basic ${encodeAuth(config.writeKey)}`,
    };
    let lastError: unknown;
    let attemptsMade = 0;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      attemptsMade = attempt + 1;
      if (attempt > 0) {
        const delay = 2 ** (attempt - 1) * 1000;
        await sleep(delay);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
        const response = await fetch(config.endpoint, {
          method: "POST",
          headers: baseHeaders,
          body,
          keepalive: true,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          return;
        }

        if (NON_RETRYABLE_STATUSES.has(response.status)) {
          if (config.debug) {
            // biome-ignore lint/suspicious/noConsole: debug mode
            console.warn(`ETS Transport: non-retryable error ${response.status}`);
          }
          throw new TransportError(
            `ETS Transport: non-retryable status ${response.status}`,
            { attempts: attemptsMade, status: response.status, kind: "non-retryable" },
          );
        }

        if (isRetryableStatus(response.status)) {
          if (config.debug) {
            // biome-ignore lint/suspicious/noConsole: debug mode
            console.warn(
              `ETS Transport: retryable error ${response.status}, attempt ${attempt + 1}/${config.maxRetries + 1}`,
            );
          }
          lastError = new TransportError(
            `ETS Transport: retryable status ${response.status}`,
            { attempts: attemptsMade, status: response.status, kind: "retry-exhausted" },
          );
          continue;
        }

        // Unknown status — treat as non-retryable
        throw new TransportError(
          `ETS Transport: unknown status ${response.status}`,
          { attempts: attemptsMade, status: response.status, kind: "non-retryable" },
        );
      } catch (err) {
        clearTimeout(timeoutId);

        // Rethrow non-retryable TransportError immediately (no more retries)
        if (err instanceof TransportError && err.kind === "non-retryable") {
          throw err;
        }

        lastError = err;

        // Network error or abort — retry if attempts remain
        if (attempt === config.maxRetries) {
          if (config.debug) {
            // biome-ignore lint/suspicious/noConsole: debug mode
            console.warn("ETS Transport: all retries exhausted after network error");
          }
          throw new TransportError("ETS Transport: all retries exhausted", {
            attempts: attemptsMade,
            kind: err instanceof TransportError ? err.kind : "network",
            cause: err,
          });
        }
      }
    }

    // Defensive — loop exits only via return (success) or throw (failure).
    throw new TransportError("ETS Transport: all retries exhausted", {
      attempts: attemptsMade,
      kind: "retry-exhausted",
      cause: lastError,
    });
  }

  return { send };
}

/**
 * Fire-and-forget send via navigator.sendBeacon.
 * Intended for use in beforeunload/visibilitychange handlers where
 * fetch may not complete. Does not support auth headers or retries.
 */
export function sendBeacon(endpoint: string, payload: TransportPayload): boolean {
  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
    return false;
  }

  const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
  return navigator.sendBeacon(endpoint, blob);
}
